import { LEGAL_OPERATOR_DEFAULT, LEGAL_VERSION, type LegalOperator } from "@animeshadow/shared";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/api";

/**
 * The site's legal documents — the user agreement (a public offer), the
 * privacy policy, the consent to personal data processing, and contacts with
 * the operator's details — as data, so one text serves the /legal page, the
 * modals behind every consent checkbox, and the links in the footer and bot.
 *
 * ⚠ Fill in the operator's details (on /legal, as admin) before taking payments: a payment provider (MulenPay)
 * and Roskomnadzor both expect a real person or company behind the site —
 * full name, status (самозанятый / ИП / ООО) and ИНН. Until then the
 * documents name "администрация сервиса Anivora".
 *
 * When a document's meaning changes, raise LEGAL_VERSION (the date): new
 * sign-ups and payments record which version they agreed to.
 */

/** Which edition of the documents is in force — stored with every consent (see packages/shared/src/legal.ts). */
export { LEGAL_VERSION };
export const LEGAL_UPDATED = "1 ноября 2026 г.";

export const SITE = {
  name: "Anivora",
  domain: "animeshadow.online",
  url: "https://animeshadow.online",
  bot: "Telegram-бот Anivora",
};

/**
 * The operator's details now live on the server (GET /api/legal), edited by
 * the admin right on /legal; these are only the defaults until it answers.
 */
let cur: LegalOperator = LEGAL_OPERATOR_DEFAULT;

/** The operator's details, kept fresh from the server. */
export function useLegalOperatorQuery() {
  return useQuery({
    queryKey: ["legal-operator"],
    queryFn: ({ signal }) => apiRequest<LegalOperator>("/legal", { signal, cache: "no-store" }),
    staleTime: 5 * 60_000,
  });
}

export function useLegalOperator(): LegalOperator {
  return useLegalOperatorQuery().data ?? LEGAL_OPERATOR_DEFAULT;
}

/** How the operator is named in the texts. */
export function operatorTitle(): string {
  if (!cur.name) return "администрация сервиса Anivora";
  return [cur.status, cur.name].filter(Boolean).join(" ") + (cur.inn ? `, ИНН ${cur.inn}` : "");
}

export type LegalDocId = "terms" | "privacy" | "consent" | "contacts" | "copyright";

export interface LegalSection {
  title: string;
  /** Paragraphs; numbered "section.n" when shown. */
  items: string[];
  list?: boolean;
}

export interface LegalDoc {
  id: LegalDocId;
  title: string;
  /** For tabs and links. */
  short: string;
  lead: string;
  sections: LegalSection[];
  plain?: boolean;
}

const op = () => operatorTitle();
const email = () => cur.email;

export function legalDocs(operator: LegalOperator = LEGAL_OPERATOR_DEFAULT): Record<LegalDocId, LegalDoc> {
  cur = operator;
  return {
    terms: {
      id: "terms",
      title: "Пользовательское соглашение (публичная оферта)",
      short: "Соглашение",
      lead: `Условия использования сервиса ${SITE.name} (${SITE.domain}) и его Telegram-бота, включая платные функции PRO и возврат оплаты.`,
      sections: [
        {
          title: "Общие положения",
          items: [
            `Сервис ${SITE.name}, доступный по адресу ${SITE.url}, и ${SITE.bot} (вместе — «Сервис») предоставляет ${op()} (далее — «Администрация»). Настоящее Соглашение регулирует отношения между Администрацией и любым лицом, использующим Сервис.`,
            "Соглашение является публичной офертой в соответствии со статьёй 437 Гражданского кодекса РФ. Акцептом оферты (статья 438 ГК РФ) считается любое из действий: регистрация в Сервисе, вход через Telegram, отметка о согласии при регистрации или оплате, оплата платных функций, а также фактическое использование Сервиса.",
            "Если вы не согласны с условиями Соглашения, прекратите использование Сервиса.",
            "К отношениям сторон, не урегулированным Соглашением, применяется законодательство Российской Федерации, в том числе Закон РФ «О защите прав потребителей» — в части, касающейся пользователей-потребителей.",
          ],
        },
        {
          title: "Термины",
          items: [
            "Пользователь — дееспособное физическое лицо, использующее Сервис.",
            "Аккаунт — учётная запись Пользователя, созданная по адресу электронной почты или через Telegram.",
            "Каталог — сведения об аниме (названия, описания, постеры, рейтинги, расписание), собранные из открытых источников.",
            "Пользовательский контент — комментарии, оценки, списки, подборки, сообщения в чатах совместного просмотра, аватары и иные материалы, размещённые Пользователем.",
            "PRO — платный набор дополнительных функций Сервиса, предоставляемый на оплаченный срок.",
            `Платёжный сервис — ${cur.paymentsName} и его партнёры (банки и платёжные организации), принимающие оплату от имени Администрации. Telegram Stars — платёжная функция Telegram для цифровых товаров и услуг с расчётом в валюте XTR.`,
          ],
        },
        {
          title: "Предмет Соглашения",
          items: [
            "Администрация предоставляет Пользователю доступ к функциям Сервиса: каталогу, поиску, персональным спискам и рекомендациям, комментариям, подборкам, совместному просмотру, уведомлениям о новых сериях и другим функциям, доступным на момент использования.",
            "Основные функции предоставляются бесплатно. Дополнительные функции PRO предоставляются за плату на условиях раздела 8.",
            "Сервис предоставляется «как есть». Состав функций может меняться: Администрация вправе добавлять, изменять и отключать функции без предварительного уведомления, сохраняя оплаченный объём PRO либо возвращая оплату за неиспользованный период.",
          ],
        },
        {
          title: "Регистрация и аккаунт",
          items: [
            "Регистрироваться в Сервисе могут лица, достигшие 14 лет. Лица младше 18 лет совершают оплату только с согласия родителей или иных законных представителей.",
            "Пользователь указывает достоверные данные, хранит пароль в тайне и отвечает за действия, совершённые в его Аккаунте. О несанкционированном доступе Пользователь сообщает Администрации без промедления.",
            "Пользователь вправе в любой момент удалить Аккаунт в настройках профиля. Удаление Аккаунта прекращает доступ к PRO без возврата за уже использованный период; возврат за неиспользованный период производится по заявлению в порядке раздела 9.",
            "Администрация вправе ограничить или заблокировать Аккаунт при нарушении Соглашения, предварительно или одновременно уведомив Пользователя по возможности.",
          ],
        },
        {
          title: "Видео, сведения о тайтлах и права третьих лиц",
          items: [
            "В Сервисе используются сведения и видеопотоки сторонних источников. Просмотр может обеспечиваться встроенным плеером поставщика или плеером Anivora, в том числе с передачей медиаданных через сервер Сервиса. Доступность источника не подтверждает разрешение на использование произведения. Права на произведения сохраняются за их правообладателями.",
            "Сведения о тайтлах получены из открытых источников и приводятся в информационных целях.",
            "Часть тайтлов отмечена знаком «18+» и предназначена для совершеннолетних зрителей. Открывая такие материалы, Пользователь подтверждает, что достиг 18 лет. Материалы порнографического характера в Сервисе не размещаются.",
            `Обращения правообладателей принимаются на ${email()}. Порядок обращения опубликован на ${SITE.url}/copyright. Такие обращения рассматриваются без необоснованных задержек с учётом установленных применимым законом сроков; общий срок ответа на обращения пользователей к ним не применяется. При подтверждении оснований Администрация исправляет сведения, удаляет материал или ограничивает доступ к нему.`,
          ],
        },
        {
          title: "Правила поведения",
          items: [
            "Запрещается размещать материалы, нарушающие законодательство РФ, права третьих лиц, а также содержащие оскорбления, угрозы, разжигание ненависти, экстремистские материалы, порнографию, спам и рекламу без согласия Администрации.",
            "Запрещается размещать персональные данные других лиц без их согласия, выдавать себя за другое лицо, создавать аккаунты для обхода блокировок или накрутки, использовать автоматизированные средства сбора данных и вмешиваться в работу Сервиса.",
            "Администрация вправе удалять Пользовательский контент, нарушающий Соглашение, ограничивать отдельные функции (например, чат) и блокировать Аккаунт. Пользователь может обжаловать такое решение, написав на " + email() + ".",
          ],
        },
        {
          title: "Пользовательский контент",
          items: [
            "Права на Пользовательский контент сохраняются за Пользователем. Размещая контент, Пользователь предоставляет Администрации безвозмездную неисключительную лицензию на его хранение, воспроизведение и показ в Сервисе на срок размещения.",
            "Пользователь самостоятельно отвечает за размещённый им контент и гарантирует, что его размещение не нарушает права третьих лиц.",
          ],
        },
        {
          title: "PRO и оплата",
          items: [
            "Состав функций PRO, тарифы, стоимость и срок действия указаны на странице «Поддержать проект» (" + SITE.url + "/support) либо в счёте официального Telegram-бота Anivora, если этот способ оплаты доступен. Стоимость фиксируется до подтверждения заказа.",
            `Оплата производится через Платёжный сервис (${cur.paymentsName}) доступными на странице оплаты способами либо через Telegram Stars в официальном Telegram-боте Anivora. Для Stars сумма и валюта XTR показываются в счёте Telegram до оплаты; данные банковских карт Администрации не передаются.`,
            "Обязательство по оплате считается исполненным после подтверждения от Платёжного сервиса либо получения и проверки Telegram update successful_payment. PRO активируется автоматически после подтверждения, как правило сразу, но не позднее 24 часов. Если PRO не активирован в этот срок, Пользователь сообщает об этом на " + email() + ".",
            "PRO предоставляется на оплаченный срок без автоматического продления, если иное прямо не указано на странице оплаты. При продлении действуют условия, опубликованные на момент новой оплаты.",
            "Если акция доступна, Пользователь может один раз получить три бесплатных дня PRO за подписку на указанный Anivora Telegram-канал. Пока подарок действует, подписка должна сохраняться; при выходе из канала неиспользованная подарочная часть прекращается. Это не сокращает отдельно оплаченный или иной PRO-доступ.",
            "Документ, подтверждающий расчёт, предоставляется Пользователю в порядке, установленном законодательством РФ и правилами выбранного способа оплаты. Квитанция Telegram по операции Stars доступна в интерфейсе Telegram.",
            "Добровольные пожертвования («угостить кофе») являются безвозмездной поддержкой проекта и не предоставляют права на PRO или иные функции, если иное не указано на странице оплаты.",
          ],
        },
        {
          title: "Отказ от PRO и возврат оплаты",
          items: [
            "Пользователь вправе отказаться от PRO в любое время, направив заявление на " + email() + " с адреса, указанного в Аккаунте, или из Аккаунта через поддержку. В заявлении указываются Аккаунт, дата и сумма платежа.",
            "Если PRO ещё не активирован, оплата возвращается полностью. Если PRO активирован, возвращается стоимость неиспользованного периода — пропорционально оставшимся дням (статья 32 Закона РФ «О защите прав потребителей»).",
            "Ошибочный, повторный платёж или платёж, по которому PRO не был предоставлен по вине Сервиса, возвращается полностью.",
            "Возврат производится тем же способом, которым была произведена оплата, в течение 10 календарных дней с даты получения заявления. Для Telegram Stars возврат проводится через платёжные средства Telegram; срок зачисления зависит от Telegram или соответствующего Платёжного сервиса.",
            "Добровольные пожертвования возвращаются только в случае ошибочного платежа по заявлению, поданному в течение 14 дней.",
            "До обращения в банк с требованием об оспаривании платежа (chargeback) Пользователь направляет заявление Администрации — это ускоряет возврат.",
          ],
        },
        {
          title: "Ответственность",
          items: [
            "Администрация прилагает разумные усилия для бесперебойной работы Сервиса, но не гарантирует отсутствие перерывов, ошибок и доступность сторонних плееров и источников данных.",
            "Администрация не отвечает за содержимое сторонних сайтов и плееров, за убытки, вызванные действиями третьих лиц или невозможностью использовать Сервис по причинам, не зависящим от Администрации. Ответственность Администрации перед Пользователем в любом случае ограничена суммой, уплаченной Пользователем за PRO за последние 12 месяцев, если иное не установлено законом.",
            "Ограничения ответственности не применяются в случаях, когда они не допускаются законодательством о защите прав потребителей.",
          ],
        },
        {
          title: "Персональные данные",
          items: [
            "Обработка персональных данных Пользователей осуществляется в соответствии с Политикой конфиденциальности и Согласием на обработку персональных данных, опубликованными на странице " + SITE.url + "/legal.",
          ],
        },
        {
          title: "Разрешение споров",
          items: [
            "Споры решаются путём переговоров. Претензия направляется на " + email() + "; срок ответа на общие обращения — 10 рабочих дней с даты получения, если применимый закон не устанавливает иной срок. Обращения правообладателей рассматриваются отдельно по правилам страницы «Правообладателям».",
            "Если спор не урегулирован, он рассматривается в суде по правилам законодательства РФ. Потребитель вправе обратиться в суд по месту своего жительства.",
          ],
        },
        {
          title: "Изменение Соглашения",
          items: [
            `Администрация вправе изменять Соглашение. Новая редакция вступает в силу с момента публикации на странице ${SITE.url}/legal, если в ней не указано иное. Дата редакции указывается в начале документа.`,
            "Продолжение использования Сервиса после публикации новой редакции означает согласие с ней. Для уже оплаченного периода PRO сохраняются условия, действовавшие на момент оплаты, если новые условия не улучшают положение Пользователя.",
          ],
        },
        {
          title: "Реквизиты и контакты",
          items: contactLines(),
        },
      ],
    },

    privacy: {
      id: "privacy",
      title: "Политика конфиденциальности",
      short: "Конфиденциальность",
      lead: `Как ${SITE.name} обрабатывает и защищает персональные данные пользователей сайта ${SITE.domain} и Telegram-бота — в соответствии с Федеральным законом № 152-ФЗ «О персональных данных».`,
      sections: [
        {
          title: "Общие положения",
          items: [
            `Настоящая Политика определяет порядок обработки и меры по защите персональных данных, которые ${op()} (далее — «Оператор») получает от пользователей сайта ${SITE.url} и ${SITE.bot} (далее — «Сервис»).`,
            "Политика разработана в соответствии с Федеральным законом от 27.07.2006 № 152-ФЗ «О персональных данных» и является общедоступным документом, определяющим политику Оператора в отношении обработки персональных данных.",
            "Используя Сервис, пользователь подтверждает, что ознакомился с Политикой. Обработка данных, для которой требуется согласие, осуществляется на основании отдельного Согласия на обработку персональных данных.",
          ],
        },
        {
          title: "Какие данные обрабатываются",
          items: [
            "Данные аккаунта: адрес электронной почты, отображаемое имя и имя пользователя, хэш пароля (сам пароль не хранится), аватар, обложка профиля, описание, настройки приватности; по желанию — дата рождения.",
            "Данные Telegram — при входе через Telegram или подключении бота: идентификатор, имя, фамилия и имя пользователя в Telegram, фотография профиля, идентификатор чата с ботом. Если акция включена, также обрабатываются статус подписки на указанный канал, даты предоставления и окончания разового подарка PRO и отметка о его использовании.",
            "Данные об использовании Сервиса: списки и оценки тайтлов, история и прогресс просмотра, комментарии и реакции, подборки, любимые моменты, подписки на пользователей, достижения, участие в комнатах совместного просмотра (сообщения в чате комнаты хранятся только пока комната открыта).",
            "Технические данные: IP-адрес, сведения о браузере и устройстве (user-agent), идентификатор браузера, сохраняемый в локальном хранилище, файлы cookie, адрес страницы перехода (referrer), просмотренные страницы, время и длительность посещений, измеренная скорость соединения в плеере.",
            "Данные для уведомлений: адрес подписки браузера на push-уведомления и ключи шифрования к ней; выбранные настройки уведомлений.",
            "Данные об оплате: сумма, дата, внутренний идентификатор и статус платежа, тариф, а для Telegram Stars — идентификатор платежа Telegram. Реквизиты банковских карт Оператор не получает и не хранит — их обрабатывает выбранная платёжная платформа.",
            "Переписка с Оператором: обращения в поддержку, жалобы, заявления о возврате.",
          ],
        },
        {
          title: "Цели обработки",
          items: [
            "Регистрация и ведение аккаунта, вход в Сервис, восстановление доступа.",
            "Предоставление функций Сервиса: списки, история просмотра, рекомендации, комментарии, совместный просмотр, расписание.",
            "Уведомления о новых сериях и о событиях в Сервисе — на сайте, в Telegram и push-уведомлениями браузера (по выбору пользователя); отправка кодов подтверждения на почту.",
            "Приём оплаты, активация PRO, возвраты, исполнение требований налогового законодательства.",
            "Проверка подписки на Telegram-канал и предоставление или отзыв оставшейся части разового подарка PRO по правилам акции.",
            "Обеспечение безопасности: защита от взлома, спама, злоупотреблений, модерация.",
            "Статистика и улучшение Сервиса — в обобщённом виде.",
            "Ответы на обращения и исполнение требований законодательства РФ.",
          ],
        },
        {
          title: "Правовые основания",
          items: [
            "Согласие субъекта персональных данных (п. 1 ч. 1 ст. 6 Закона № 152-ФЗ) — для регистрации, уведомлений, аналитики и иной обработки, требующей согласия.",
            "Исполнение договора (Пользовательского соглашения), стороной которого является пользователь (п. 5 ч. 1 ст. 6).",
            "Исполнение обязанностей, возложенных на Оператора законодательством РФ (п. 2 ч. 1 ст. 6).",
          ],
        },
        {
          title: "Порядок и сроки обработки",
          items: [
            "Обработка ведётся с использованием средств автоматизации и включает сбор, запись, систематизацию, накопление, хранение, уточнение, извлечение, использование, передачу (предоставление, доступ), обезличивание, блокирование, удаление и уничтожение.",
            "Запись, систематизация, накопление, хранение, уточнение и извлечение персональных данных граждан РФ осуществляются с использованием баз данных, находящихся на территории Российской Федерации (ч. 5 ст. 18 Закона № 152-ФЗ).",
            "Данные аккаунта хранятся, пока существует аккаунт, и уничтожаются в течение 30 дней после его удаления или отзыва согласия, если более длительный срок не требуется по закону (например, сведения об оплатах — в сроки, установленные налоговым законодательством).",
            "Технические журналы хранятся не более 90 дней. Статистика посещений хранится в обезличенном или обобщённом виде.",
            "Данные о сессиях совместного просмотра (кто и сколько смотрел) хранятся не более 30 дней; сообщения чата комнаты удаляются при её закрытии.",
          ],
        },
        {
          title: "Передача данных третьим лицам",
          items: [
            "Оператор не продаёт персональные данные и передаёт их только в объёме, необходимом для работы Сервиса:",
            "— хостинг-провайдеру, на серверах которого в РФ размещён Сервис;",
            `— Платёжному сервису ${cur.paymentsName} и его партнёрам — для проведения оплаты и возвратов;`,
            "— сервису отправки электронной почты — для доставки кодов подтверждения и писем Сервиса;",
            "— Telegram — при входе через Telegram, для сообщений подключённого бота, а также для проведения и возврата платежей Stars, если пользователь выбрал этот способ оплаты;",
            "— службам push-уведомлений браузеров (Google, Mozilla, Apple, Microsoft) — для доставки уведомлений, если пользователь их включил;",
            "— сервисам веб-аналитики (Яндекс.Метрика, Google Analytics — если подключены) — обезличенные данные о посещениях;",
            "— по запросу уполномоченных государственных органов в случаях, предусмотренных законом.",
            "Отдельные получатели (сервис отправки почты, Google Analytics, службы push-уведомлений) могут находиться за пределами РФ. Трансграничная передача осуществляется с согласия пользователя и в порядке, установленном статьёй 12 Закона № 152-ФЗ.",
            "Встроенные видеоплееры загружаются со сторонних сайтов и получают IP-адрес и технические данные браузера напрямую — в соответствии с их собственными политиками. Оператор не передаёт им данные аккаунта.",
          ],
        },
        {
          title: "Cookie и похожие технологии",
          items: [
            "Сервис использует cookie и локальное хранилище браузера для входа в аккаунт, сохранения настроек (тема, громкость, качество, фильтры картинки), идентификатора браузера для статистики, а также для работы сервисов аналитики.",
            "Пользователь может запретить cookie в настройках браузера; при этом часть функций (например, вход в аккаунт) может работать некорректно.",
          ],
        },
        {
          title: "Права пользователя",
          items: [
            "Пользователь вправе получить сведения об обработке своих персональных данных, потребовать их уточнения, блокирования или уничтожения, если они неполные, устаревшие, неточные или обрабатываются незаконно, а также отозвать согласие на обработку.",
            `Запрос направляется на ${email()}. Оператор отвечает в течение 10 рабочих дней с даты получения запроса; срок может быть продлён не более чем на 5 рабочих дней с уведомлением о причинах.`,
            "Удалить аккаунт и связанные с ним данные можно самостоятельно в настройках профиля. Отключить уведомления можно в настройках профиля, в боте или в браузере.",
            "Пользователь вправе обжаловать действия Оператора в Роскомнадзоре (rkn.gov.ru) или в суде.",
          ],
        },
        {
          title: "Защита данных",
          items: [
            "Оператор принимает правовые, организационные и технические меры защиты: передача данных по защищённому протоколу HTTPS, хранение паролей только в виде стойкого хэша, ограничение доступа к данным, журналирование действий администраторов.",
          ],
        },
        {
          title: "Несовершеннолетние",
          items: [
            "Сервис не предназначен для регистрации детей младше 14 лет. Если стало известно, что аккаунт создан ребёнком младше 14 лет без согласия законного представителя, Оператор удаляет такой аккаунт по обращению.",
          ],
        },
        {
          title: "Изменение Политики",
          items: [
            `Оператор вправе изменять Политику. Новая редакция вступает в силу с момента публикации на странице ${SITE.url}/legal. Дата редакции указывается в начале документа.`,
          ],
        },
        {
          title: "Контакты Оператора",
          items: contactLines(),
        },
      ],
    },

    consent: {
      id: "consent",
      title: "Согласие на обработку персональных данных",
      short: "Согласие",
      lead: "Даётся при регистрации, входе через Telegram, подключении уведомлений и оплате — отметкой в соответствующей форме.",
      sections: [
        {
          title: "Кому даётся согласие",
          items: [
            `Я, действуя свободно, своей волей и в своём интересе, в соответствии со статьёй 9 Федерального закона № 152-ФЗ «О персональных данных» даю согласие оператору персональных данных — ${op()} (далее — «Оператор») — на обработку моих персональных данных на условиях, указанных ниже.`,
          ],
        },
        {
          title: "Перечень персональных данных",
          items: [
            "Адрес электронной почты, отображаемое имя и имя пользователя, аватар и иные данные профиля, которые я укажу; данные Telegram (идентификатор, имя, имя пользователя, фото) — при входе через Telegram; данные о моих действиях в Сервисе (списки, оценки, история просмотра, комментарии); технические данные (IP-адрес, сведения о браузере и устройстве, cookie, идентификатор браузера); данные о платежах (без реквизитов карт); адрес подписки на push-уведомления.",
          ],
        },
        {
          title: "Цели обработки",
          items: [
            `Регистрация и ведение аккаунта в Сервисе ${SITE.name}; предоставление функций Сервиса и рекомендаций; направление уведомлений о новых сериях и событиях в Сервисе по выбранным мной каналам; приём оплаты и активация PRO; обеспечение безопасности; обезличенная статистика; ответы на мои обращения.`,
          ],
        },
        {
          title: "Действия с данными",
          items: [
            "Сбор, запись, систематизация, накопление, хранение, уточнение (обновление, изменение), извлечение, использование, передача (предоставление, доступ) лицам, указанным в Политике конфиденциальности, обезличивание, блокирование, удаление, уничтожение — с использованием средств автоматизации.",
            "Я согласен(на) на трансграничную передачу данных получателям, указанным в Политике конфиденциальности, в объёме, необходимом для работы Сервиса (доставка писем, push-уведомлений, веб-аналитика).",
          ],
        },
        {
          title: "Срок действия и отзыв",
          items: [
            "Согласие действует до удаления аккаунта или до его отзыва.",
            `Согласие может быть отозвано письмом на ${email()} или удалением аккаунта в настройках профиля. После отзыва Оператор прекращает обработку и уничтожает данные в течение 30 дней, кроме данных, хранение которых обязательно по закону.`,
          ],
        },
        {
          title: "Подтверждение",
          items: [
            "Я подтверждаю, что мне исполнилось 14 лет, что ознакомлен(а) с Политикой конфиденциальности и Пользовательским соглашением и что указанные мной данные принадлежат мне.",
          ],
        },
      ],
    },

    copyright: {
      id: "copyright",
      title: "Информация для правообладателей",
      short: "Правообладателям",
      plain: true,
      lead: "Сбор информации в сети и размещение сведений в каталоге Anivora производятся в автоматическом режиме. На сайте представлен контент с любительским переводом для ознакомительного просмотра.",
      sections: [
        {
          title: "",
          items: [
            `Администрация Anivora предлагает сотрудничество с правообладателями контента. Если вы обнаружили материал, представленный на нашем сайте, который нарушает ваши авторские права или дискредитирует вашу компанию, предоставляя неверную или искажённую информацию, пожалуйста, свяжитесь с нами для решения этого вопроса. Для этого отправьте письмо на адрес ${email()}, по возможности с корпоративного почтового ящика, содержащее:`,
          ],
        },
        {
          title: "",
          list: true,
          items: [
            "Контактные данные, реквизиты вашей компании.",
            "Прямую ссылку на материал, который вы считаете спорным.",
            "Заверенные сканированные копии документов, подтверждающих ваше эксклюзивное право на материал.",
            "Если вы представляете интересы другой компании — нотариально заверенные копии документов на посреднические услуги.",
          ],
        },
        {
          title: "",
          items: [
            "Вся информация будет проверена, и администрация Anivora в кратчайшие сроки свяжется с вами для урегулирования спорного вопроса.",
            "Мы не продаём материалы на нашем сайте. Контент с любительским переводом предоставлен для бесплатного домашнего ознакомительного просмотра.",
          ],
        },
      ],
    },

    contacts: {
      id: "contacts",
      title: "Контакты и реквизиты",
      short: "Контакты",
      lead: "Как связаться с администрацией сервиса — по вопросам оплаты, возвратов, персональных данных и правообладателям.",
      sections: [
        { title: "Оператор сервиса", items: contactLines() },
        {
          title: "Куда писать",
          items: [
            `Поддержка, оплата и возвраты — ${email()}. Ответ — в течение 10 рабочих дней, обычно быстрее.`,
            `Персональные данные (запросы, отзыв согласия) — ${email()}, тема письма «Персональные данные».`,
            `Правообладателям — ${email()}, тема письма «Обращение правообладателя — Anivora». Порядок обращения: ${SITE.url}/copyright. Общий срок ответа поддержки не применяется к требованиям с установленным законом сроком.`,
            ...(cur.telegram ? [`Новости проекта — Telegram-канал ${cur.telegram}.`] : []),
          ],
        },
        {
          title: "Оплата",
          items: [
            `Платежи принимает Платёжный сервис ${cur.paymentsName || "оператора оплаты"}${cur.paymentsUrl ? ` (${cur.paymentsUrl})` : ""} и его партнёры; при выборе Telegram Stars оплата проходит через официальный Telegram-бот Anivora. Данные карт вводятся на защищённой странице Платёжного сервиса и Администрации не передаются.`,
            "Условия оплаты и возврата — в разделах 8 и 9 Пользовательского соглашения.",
          ],
        },
      ],
    },
  };
}

function contactLines(): string[] {
  return [
    cur.name ? `${cur.status ? `${capital(cur.status)}: ` : ""}${cur.name}` : `Администрация сервиса ${SITE.name}`,
    ...(cur.inn ? [`ИНН: ${cur.inn}`] : []),
    ...(cur.ogrn ? [`ОГРН/ОГРНИП: ${cur.ogrn}`] : []),
    ...(cur.address ? [`Адрес для корреспонденции: ${cur.address}`] : []),
    `Электронная почта: ${cur.email}`,
    `Сайт: ${SITE.url}`,
  ];
}

function capital(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Consent to payment terms given in this browser, for this edition. */
const PAY_KEY = "as:pay-consent";
export function payConsentGiven(): boolean {
  try {
    return localStorage.getItem(PAY_KEY) === LEGAL_VERSION;
  } catch {
    return false;
  }
}
export function givePayConsent(): void {
  try {
    localStorage.setItem(PAY_KEY, LEGAL_VERSION);
  } catch {
    // No storage: asked again next time.
  }
}
